import React, {useContext, useState} from "react";
import "./DisplayBooking.css";
import {Icon, TextArea} from "@spg/component_library";
import {FormattedMessage, useIntl} from "react-intl";
import {SnackbarContext} from "../../utility/SnackbarProvider";
import {Link} from "react-router-dom";
import chevron from "../../resources/images/chevron.svg";
import {Calendar} from "primereact/calendar";
import {FileUpload} from "./FileUpload";
import {getLocaleFromAvailable} from "../../logic/Locale";
import {KeycloakContext} from "../../utility/KeycloakProvider";
import {AppSettingsContext} from "../../utility/AppSettingsProvider";

/**
 * A page allowing users to open a request to display an image or video on a digital stand
 */
function DisplayBooking() {
  const {addSnackbar} = useContext(SnackbarContext);
  const {keycloak} = useContext(KeycloakContext);
  const {digitalStand} = useContext(AppSettingsContext);
  const intl = useIntl();

  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [note, setNote] = useState("");

  const [waitingForResponse, setWaitingForResponse] = useState(false);

  const locale = getLocaleFromAvailable();

  return (
    <div className="displayBooking page__grid">
      <div className="displayBooking__title">
        <h1 className="displayBooking__header" id="displayBookingTitle">
          <Link to="/" className="displayBooking__back">
            <Icon
              href={chevron + "#chevronLeft"}
              title={intl.formatMessage({id: "displayBooking.actions.back"})}
              strokeColor="#101223"
            />
          </Link>
          <FormattedMessage id="displayBooking.titles.page" />
        </h1>
        <Link
          to={digitalStand.statsUrl}
          className="styleLinkAsButton secondary"
        >
          <FormattedMessage id="displayBooking.titles.dashboard" />
        </Link>
      </div>
      <div className="displayBooking__info">
        <FormattedMessage
          id="displayBooking.paragraphs.info"
          values={{
            a: (content) => (
              <a className="displayBooking__link" href={digitalStand.wikiUrl}>
                {content}
              </a>
            ),
            br: () => <br />,
          }}
        />
      </div>
      {!keycloak?.tokenParsed?.fachbereich ? (
        <div className="displayBooking__info">
          <FormattedMessage
            id="displayBooking.paragraphs.warning"
            values={{
              a: (content) => (
                <a
                  className="displayBooking__link"
                  href={keycloak.createAccountUrl({
                    redirectUri: window.location.origin + "/digital-stand",
                  })}
                >
                  {content}
                </a>
              ),
            }}
          />
        </div>
      ) : (
        <div />
      )}
      <div className="displayBooking__input displayBooking__input--startDate">
        <label htmlFor="startDate">
          <FormattedMessage id="displayBooking.inputs.startDate" />
          {""}
        </label>
        <Calendar
          id="startDate"
          dateFormat="dd.mm.yy"
          value={startDate}
          disabled={waitingForResponse}
          placeholder={intl.formatMessage({
            id: "displayBooking.inputs.startDate",
          })}
          onChange={(e) => {
            if (e.value) {
              setStartDate(e.value);
              if (endDate && endDate < e.value) setEndDate(undefined);
            }
          }}
          showIcon
          locale={locale}
          icon={() => (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.75 2C6.16421 2 6.5 2.33579 6.5 2.75V4H13.5V2.75C13.5 2.33579 13.8358 2 14.25 2C14.6642 2 15 2.33579 15 2.75V4H15.25C16.7688 4 18 5.23122 18 6.75V15.25C18 16.7688 16.7688 18 15.25 18H4.75C3.23122 18 2 16.7688 2 15.25V6.75C2 5.23122 3.23122 4 4.75 4H5V2.75C5 2.33579 5.33579 2 5.75 2ZM4.75 7.5C4.05964 7.5 3.5 8.05964 3.5 8.75V15.25C3.5 15.9404 4.05964 16.5 4.75 16.5H15.25C15.9404 16.5 16.5 15.9404 16.5 15.25V8.75C16.5 8.05964 15.9404 7.5 15.25 7.5H4.75Z"
              />
            </svg>
          )}
        />
      </div>
      <div className="displayBooking__input displayBooking__input--endDate">
        <label htmlFor="endDate">
          <FormattedMessage id="displayBooking.inputs.endDate" />
          {""}
        </label>
        <Calendar
          id="endDate"
          dateFormat="dd.mm.yy"
          value={endDate}
          disabled={waitingForResponse}
          placeholder={intl.formatMessage({
            id: "displayBooking.inputs.endDate",
          })}
          onChange={(e) => {
            if (e.value) setEndDate(e.value);
          }}
          showIcon
          minDate={startDate}
          locale={locale}
          icon={() => (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.75 2C6.16421 2 6.5 2.33579 6.5 2.75V4H13.5V2.75C13.5 2.33579 13.8358 2 14.25 2C14.6642 2 15 2.33579 15 2.75V4H15.25C16.7688 4 18 5.23122 18 6.75V15.25C18 16.7688 16.7688 18 15.25 18H4.75C3.23122 18 2 16.7688 2 15.25V6.75C2 5.23122 3.23122 4 4.75 4H5V2.75C5 2.33579 5.33579 2 5.75 2ZM4.75 7.5C4.05964 7.5 3.5 8.05964 3.5 8.75V15.25C3.5 15.9404 4.05964 16.5 4.75 16.5H15.25C15.9404 16.5 16.5 15.9404 16.5 15.25V8.75C16.5 8.05964 15.9404 7.5 15.25 7.5H4.75Z"
              />
            </svg>
          )}
        />
      </div>
      <div className="displayBooking__input displayBooking__input--note">
        <TextArea
          label={intl.formatMessage({
            id: "displayBooking.inputs.note",
          })}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          rows={6}
          disabled={waitingForResponse}
          maxLength={100000}
        />
      </div>
      <div className="displayBooking__file">
        <FileUpload
          startDate={startDate}
          endDate={endDate}
          note={note}
          setWaitingForResponse={setWaitingForResponse}
          onSuccess={() => {
            setStartDate(undefined);
            setEndDate(undefined);
            setNote("");
            addSnackbar({
              timeout: 7000,
              variant: "timeout",
              text: intl.formatMessage({id: "displayBooking.messages.success"}),
            });
          }}
        />
      </div>
    </div>
  );
}

export default DisplayBooking;
